import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../contexts/TranslationContext';
import PageHero from '../../components/ui/page-hero';
import StrategicPillars from '../../components/Vision30Maroc/StrategicPillars';
import Impact from '../../components/Vision30Maroc/Impact';
import MoroccoInAction from '../../components/Vision30Maroc/MoroccoInAction';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Mail, Phone, MapPin } from 'lucide-react';

const Vision30Maroc = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>Vision 2030 Morocco | Digital Transformation Strategy | Envael</title>
        <meta name="description" content="Explore Morocco's Vision 2030 digital transformation strategy with Envael. Discover how we're contributing to Morocco's technological advancement and digital economy goals." />
        <meta name="keywords" content="vision 2030 morocco, digital transformation morocco, morocco digital strategy, technological advancement morocco, digital economy morocco, envael vision 2030" />
        <meta property="og:title" content="Vision 2030 Morocco | Digital Transformation Strategy | Envael" />
        <meta property="og:description" content="Explore Morocco's Vision 2030 digital transformation strategy with Envael. Discover how we're contributing to Morocco's technological advancement." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/vision-2030" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vision 2030 Morocco | Digital Transformation Strategy | Envael" />
        <meta name="twitter:description" content="Explore Morocco's Vision 2030 digital transformation strategy with Envael." />
        <link rel="canonical" href="https://envael.com/vision-2030" />
      </Helmet>

      <div className="min-h-screen bg-background">
      {/* Page Hero */}
      <PageHero 
        pageName={t('pages.vision2030.title')} 
        subtitle={t('pages.vision2030.subtitle')}
        size="large"
        fullHeight={false}
      />


      {/* Strategic Pillars */}
      <StrategicPillars />

      {/* Impact Section */}
      <Impact />

      {/* Morocco in Action */}
      <MoroccoInAction />

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('vision30.finalCta.title')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            {t('vision30.finalCta.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button size="lg" className="px-8 py-4 text-lg font-semibold">
              {t('vision30.finalCta.primary')}
            </Button>
            <Button variant="outline" size="lg" className="px-8 py-4 text-lg font-semibold">
              {t('vision30.finalCta.secondary')}
            </Button>
          </div>

          {/* Contact Information */}
          <Card className="bg-background/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {t('vision30.contact.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="flex items-center justify-center text-primary font-semibold mb-2">
                    <Mail className="w-5 h-5 mr-2" />
                    Email
                  </div>
                  <div className="text-muted-foreground">contact@envael.com</div>
                </div>
                <div>
                  <div className="flex items-center justify-center text-primary font-semibold mb-2">
                    <Phone className="w-5 h-5 mr-2" />
                    Phone
                  </div>
                  <div className="text-muted-foreground">+212 6 63 08 13 83</div>
                </div>
                <div>
                  <div className="flex items-center justify-center text-primary font-semibold mb-2">
                    <MapPin className="w-5 h-5 mr-2" />
                    Location
                  </div>
                  <div className="text-muted-foreground">287 BLOC K APPT 2 ETAGE 1 OULED OUJIH KENITRA</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted/30 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/Logos/Asset 1front.svg"
                alt="Envael Logo"
                className="w-8 h-8 mr-3"
              />
              <span className="text-xl font-bold text-foreground">Envael</span>
            </div>
            <p className="text-muted-foreground mb-4">
              {t('vision30.footer.tagline')}
            </p>
            <div className="flex justify-center space-x-6 text-sm text-muted-foreground">
              <span>© 2024 Envael</span>
              <span>•</span>
              <span>{t('vision30.footer.rights')}</span>
              <span>•</span>
              <span>{t('vision30.footer.morocco')}</span>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default Vision30Maroc;
